export const WELCOME_ROUTE = "/welcome-display";
export const WELCOME_WINDOW_NAME = "dcWelcome";

const CHANNEL_NAME = "dc-welcome";
const STORAGE_KEY = "dc.welcomeQueue";
const COMMAND_KEY = "dc.welcomeCmd";
const SELECTION_KEY = "dc.welcomeSel";

let welcomeWin = null; // module-level handle to the opened window (sender side)

function makeChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch (e) {
    return null;
  }
}

// Persist (so a freshly opened window has data on mount) then broadcast (for an
// already-open window). Shared by the open + publish paths below.
function sendQueue(message) {
  const stamped = { ...message, postedAt: Date.now() };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stamped));
  } catch (e) {
    /* ignore quota/availability errors */
  }

  const ch = makeChannel();
  if (ch) {
    ch.postMessage(stamped);
    ch.close();
  }
}

// --- Sender side (dashboard) ---

// Open or focus the welcome window and seed it with the queue. `activeId` sets
// the initial selection (an appointmentId, or null for the general screen).
export function openWelcomeQueue({ clinic, patients, activeId }) {
  sendQueue({ clinic, patients, activeId });

  // Reuse the existing window if we still hold a live handle (no reload),
  // otherwise open the fixed-name window (reuses any same-named window).
  if (welcomeWin && !welcomeWin.closed) {
    welcomeWin.focus();
    return true;
  }
  welcomeWin = window.open(WELCOME_ROUTE, WELCOME_WINDOW_NAME);
  if (welcomeWin) {
    welcomeWin.focus();
    return true;
  }
  return false; // popup blocked
}

// Live refresh of the queue without opening a window or changing selection.
// `activeId` is intentionally omitted so the receiver keeps its current pick.
export function publishWelcomeQueue({ clinic, patients }) {
  sendQueue({ clinic, patients });
}

// Remote-control the already-open welcome board (e.g. the dashboard's arrow
// keys). `action` is one of "next" | "prev" | "postopNext" | "postopPrev".
// Commands are live-only: they go over the BroadcastChannel (primary) with a
// transient localStorage key as a fallback for browsers without it. We never
// touch STORAGE_KEY so the seeded queue stays intact.
export function publishWelcomeCommand(action) {
  const stamped = { type: "nav", action, postedAt: Date.now() };

  const ch = makeChannel();
  if (ch) {
    ch.postMessage(stamped);
    ch.close();
  }

  try {
    localStorage.setItem(COMMAND_KEY, JSON.stringify(stamped));
  } catch (e) {
    /* ignore quota/availability errors */
  }
}

// Reverse report from the welcome board back to the dashboard: which patient is
// currently on screen (an appointmentId, or null for the general screen). Lets
// the dashboard highlight the matching "open welcome" button. Live-only, same
// shape as commands — never touches the queue STORAGE_KEY.
export function publishWelcomeSelection(activeId) {
  const stamped = { type: "selection", activeId, postedAt: Date.now() };

  const ch = makeChannel();
  if (ch) {
    ch.postMessage(stamped);
    ch.close();
  }

  try {
    localStorage.setItem(SELECTION_KEY, JSON.stringify(stamped));
  } catch (e) {
    /* ignore quota/availability errors */
  }
}

// --- Receiver side (welcome board) ---
export function readLastQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function subscribeWelcomeQueue(callback) {
  // De-dupe per message type. A single send fires on both the BroadcastChannel
  // and the storage event with the same (type, postedAt), so same-type dupes are
  // dropped — but a "nav" echo no longer blocks a near-simultaneous "selection"
  // report (they advance independent stamps).
  const lastStamp = {};
  const cleanups = [];

  const deliver = (message) => {
    if (!message) return;
    const type = message.type || "queue";
    const stamp = message.postedAt || 0;
    if (stamp && stamp <= (lastStamp[type] || 0)) return;
    lastStamp[type] = stamp || lastStamp[type] || 0;
    callback(message);
  };

  const ch = makeChannel();
  if (ch) {
    ch.onmessage = (e) => deliver(e.data);
    cleanups.push(() => ch.close());
  }

  const onStorage = (e) => {
    if (
      (e.key === STORAGE_KEY ||
        e.key === COMMAND_KEY ||
        e.key === SELECTION_KEY) &&
      e.newValue
    ) {
      try {
        deliver(JSON.parse(e.newValue));
      } catch (err) {
        /* ignore */
      }
    }
  };
  window.addEventListener("storage", onStorage);
  cleanups.push(() => window.removeEventListener("storage", onStorage));

  return () => cleanups.forEach((fn) => fn());
}
